<template>
  <div class="report-page">
    <PageHeader header="Reports" icon="mdi-chart-box-outline" />
    <PageContent>
      <div class="report-content">
        <!-- Loading -->
        <div v-if="loading" class="report-loading">
          <v-skeleton-loader type="image" height="120" class="mb-4" />
          <v-skeleton-loader type="image" height="200" class="mb-4" />
          <v-skeleton-loader type="list-item-two-line@4" />
        </div>

        <template v-else>
          <!-- Empty state -->
          <div v-if="surveys.length === 0" class="report-empty">
            <v-icon size="56" color="#CFD8DC">mdi-chart-box-outline</v-icon>
            <h3>No data to report</h3>
            <p>Create a survey to start seeing analytics here.</p>
          </div>

          <template v-else>
            <!-- Summary cards -->
            <div class="report-stats">
              <StatCard
                icon="mdi-clipboard-text-multiple-outline"
                :value="surveys.length"
                label="Surveys"
                icon-color="#1565C0"
                icon-bg="rgba(21,101,192,0.1)"
              />
              <StatCard
                icon="mdi-map-marker-check-outline"
                :value="totalVisited"
                label="Places Visited"
                icon-color="#2E7D32"
                icon-bg="rgba(46,125,50,0.1)"
              />
              <StatCard
                icon="mdi-map-marker-outline"
                :value="totalPlaces"
                label="Total Places"
                icon-color="#00897B"
                icon-bg="rgba(0,137,123,0.1)"
              />
              <StatCard
                icon="mdi-percent-outline"
                :value="overallPercent + '%'"
                label="Overall Completion"
                icon-color="#F57C00"
                icon-bg="rgba(245,124,0,0.1)"
              />
            </div>

            <!-- Aggregate response distribution -->
            <div class="report-section">
              <h3 class="report-section-title">Response Distribution</h3>
              <p class="report-section-sub">Across all {{ surveys.length }} surveys</p>
              <div class="report-card">
                <DonutChart
                  :data="responseData"
                  :size="180"
                  center-label="responses"
                  layout="row"
                />
              </div>
            </div>

            <!-- Survey completion comparison -->
            <div class="report-section">
              <h3 class="report-section-title">Completion by Survey</h3>
              <p class="report-section-sub">Click a survey to open it</p>
              <div class="report-card">
                <BarChart
                  :data="completionData"
                  :max="100"
                  suffix="%"
                  clickable
                  @select="openSurvey"
                />
              </div>
            </div>

            <!-- Surveyor / place totals -->
            <div class="report-section">
              <h3 class="report-section-title">Places by Survey</h3>
              <div class="report-card">
                <BarChart
                  :data="placeCountData"
                  color="#00897B"
                  clickable
                  @select="openSurvey"
                />
              </div>
            </div>
          </template>
        </template>
      </div>
    </PageContent>
  </div>
</template>

<script>
import _ from "lodash";

import PageContent from "@/components/pages/PageContent.vue";
import PageHeader from "@/components/pages/PageHeader.vue";
import StatCard from "@/components/viz/StatCard.vue";
import DonutChart from "@/components/viz/DonutChart.vue";
import BarChart from "@/components/viz/BarChart.vue";

import PlaceUtil from "@/util/PlaceUtil";
import SurveyService from "@/services/SurveyService";

import { NavigationStore } from "@/stores/NavigationStore.js";
import { OrgInfoStore } from "@/stores/OrgInfoStore.js";
import { SurveyStore } from "@/stores/SurveyStore.js";

export default {
  name: "ReportPage",
  components: { PageContent, PageHeader, StatCard, DonutChart, BarChart },
  data() {
    return {
      loading: true,
      surveys: [],
      OrgInfoStore,
    };
  },
  computed: {
    totalPlaces() {
      return this.surveys.reduce((sum, s) => sum + s.placeCount, 0);
    },
    totalVisited() {
      return this.surveys.reduce((sum, s) => sum + s.visitedCount, 0);
    },
    overallPercent() {
      if (this.totalPlaces === 0) return 0;
      return Math.round((this.totalVisited / this.totalPlaces) * 100);
    },
    responseData() {
      // Aggregate response code counts across all surveys
      const counts = {};
      this.surveys.forEach((s) => {
        _.forEach(s.places, (place) => {
          const abbr = _.get(place, "responseCode.abbreviation", "NR");
          counts[abbr] = (counts[abbr] || 0) + 1;
        });
      });
      return Object.keys(counts)
        .map((abbr) => ({
          label: this.responseLabel(abbr),
          value: counts[abbr],
          color: this.responseColor(abbr),
        }))
        .sort((a, b) => b.value - a.value);
    },
    completionData() {
      return this.surveys
        .map((s) => ({
          label: s.name,
          value: s.percent,
          color: this.ringColor(s.percent),
          _survey: s,
        }))
        .sort((a, b) => b.value - a.value);
    },
    placeCountData() {
      return this.surveys
        .map((s) => ({ label: s.name, value: s.placeCount, _survey: s }))
        .sort((a, b) => b.value - a.value);
    },
  },
  methods: {
    ringColor(percent) {
      if (percent >= 80) return "#2E7D32";
      if (percent >= 40) return "#1565C0";
      return "#F57C00";
    },
    responseColor(abbr) {
      const orgColor = _.get(OrgInfoStore.responseCodeColors, abbr);
      if (orgColor) return orgColor;
      return PlaceUtil.responseToColor(abbr);
    },
    responseLabel(abbr) {
      const code = _.find(
        OrgInfoStore.responseCodes,
        (c) => c.abbreviation === abbr
      );
      if (code && code.response) return code.response;
      if (abbr === "NR") return "No Response";
      return abbr;
    },
    async fetchSurveys() {
      this.loading = true;
      try {
        const data = await SurveyService.getSurveys(0, 100, "", "", "desc", false);
        const results = _.get(data, "results", []);
        this.surveys = results.map((s) => {
          const places = s.places || {};
          const placeCount = Object.keys(places).length;
          const visitedCount = _.filter(
            places,
            (p) => _.get(p, "responseCode.abbreviation", "NR") !== "NR"
          ).length;
          return {
            ...s,
            percent: Number(s.percent || 0) * 100,
            placeCount,
            visitedCount,
          };
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    openSurvey(item) {
      const survey = item._survey;
      if (!survey) return;
      SurveyStore.pendingSurvey = survey;
      NavigationStore.toggleItem("items.surveys");
    },
  },
  mounted() {
    this.fetchSurveys();
  },
};
</script>

<style scoped>
.report-page {
  height: 100%;
}

.report-content {
  padding: 16px;
}

.report-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 28px;
}

.report-section {
  margin-bottom: 28px;
}

.report-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #37474F;
  margin: 0;
}

.report-section-sub {
  font-size: 0.8rem;
  color: #90A4AE;
  margin: 2px 0 12px;
}

.report-card {
  background: #fff;
  border: 1px solid #ECEFF1;
  border-radius: 12px;
  padding: 20px;
}

.report-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  color: #90A4AE;
  text-align: center;
}

.report-empty h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #546E7A;
  margin: 16px 0 4px;
}

.report-empty p {
  font-size: 0.875rem;
  margin: 0;
  max-width: 280px;
}

@media (max-width: 600px) {
  .report-stats {
    grid-template-columns: 1fr;
  }
}
</style>
